import { useEffect, useState } from 'react'

const REFRESH_INTERVAL = 1000

interface UseCooldownOptions {
  key: string
  duration?: number
}

/**
 * Custom hook that manages a cooldown timer for buttons or actions.
 * Uses sessionStorage to persist cooldown state across page refreshes.
 *
 * @param options - Configuration object
 * @param options.key - Unique storage key to identify this cooldown instance
 * @param options.duration - Cooldown duration in seconds (default: 10)
 * @returns Object containing current cooldown seconds and function to start cooldown
 */

export const useCooldownButton = ({ key, duration = 10 }: UseCooldownOptions) => {
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    const disabledUntil = Number(sessionStorage.getItem(key))

    if (Number.isNaN(disabledUntil)) {
      sessionStorage.removeItem(key)
      return
    }

    const secondsLeft = Math.max(0, Math.ceil((disabledUntil - Date.now()) / 1000))

    if (secondsLeft > 0) {
      setCooldown(secondsLeft)
    } else {
      sessionStorage.removeItem(key)
    }
  }, [key])

  useEffect(() => {
    if (cooldown <= 0) return

    const id = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          sessionStorage.removeItem(key)
          clearInterval(id)
          return 0
        }
        return prev - 1
      })
    }, REFRESH_INTERVAL)

    return () => clearInterval(id)
  }, [cooldown, key])

  const startCooldown = () => {
    const disabledUntil = Date.now() + duration * 1000

    if (sessionStorage.getItem(key)) {
      return
    }

    sessionStorage.setItem(key, String(disabledUntil))
    setCooldown(duration)
  }

  return { cooldown, startCooldown }
}
